// 步骤组件公共样式
import type { ApplyMembershipLevel } from '../utils';

const stepCommonClassName = {
  // 标题样式
  stepTitle:
    'relative max-xl:px-[14px] pb-[20px] xl:pb-[30px] text-left f20 font-bb text-primary-black flex justify-between items-center',

  // 图标样式
  icon: 'text-auxiliary-black f15 cursor-pointer',
  questionIcon: 'ml-[4px] f16 cursor-pointer relative text-[#808080]',
  arrowIcon: 'text-[grey] f14',

  // 选项容器样式
  optionsContainer: 'mx-auto max-xl:px-[14px] flex flex-col items-center',

  // 选项项目(最外层)
  optionItem: 'relative w-full mb-[10px] xl:mb-[12px]',
  recommendedItem: 'mt-[20px] xl:mt-0',

  // 选项盒子（仅次于选项项目，包含有一二级。除右上角标签外，其他包含内容跟选项项目一样）
  optionBox:
    'relative z-[10] box-border bg-white border border-[#d4d4d4] xl:border-[transparent] rounded-[6px] overflow-hidden cursor-pointer hover:border-[#d4d4d4] hover:border-[1px] xl:border-[1px] xl:hover:border-[#d4d4d4]',
  selectedOptionBox: 'border-[1.5px] !border-primary-purple hover:border-primary-purple',

  // 选项内容容器（仅单级的盒子）
  optionContent: 'flex items-center p-[14px_20px_20px_14px]',

  // 左侧图片容器
  imageContainer: 'flex items-center justify-center mr-[20px] self-start',

  // 右侧文本内容容器
  textContainer: 'w-[calc(100%-50px-24px)] xl:flex xl:justify-between',

  // 中间内容容器
  textCenterContainer: 'xl:flex-1 xl:max-w-[352px]',

  // 标题容器
  titleContainer: 'flex items-center justify-start font-bs text-primary-black f14 mb-[8px]',

  // 标题图片
  titleImage: 'mt-[2px] w-[auto] h-[21px]',

  // 描述文本
  description: (_waiMode: boolean) =>
    'f12 font-br text-primary-black mt-[8px] text-left leading-[20px]',

  // 价格容器
  priceContainer: 'flex shrink-0 items-center max-xl:mt-[10px] f14 xl:ml-[60px] xl:pl-[30px]',

  // 包含划线价与售价的小盒子
  priceInnerBox: 'xl:flex flex-col items-end justify-between gap-[6px]',

  // 推荐标签
  recommendBadge:
    'absolute top-[-20px] xl:top-[-8px] left-0 w-full xl:w-[auto] xl:px-[14px] xl:rounded-[6px_10px_10px_0] xl:z-[15] px-[10px] pb-[10px] xl:pb-0 bg-primary-purple rounded-t-[6px] text-white box-border text-center f11',
  // 推荐标签内容
  recommendBadgeContent: 'h-[20px] flex items-center justify-start',

  // 价格文本
  priceText: 'text-primary-black font-bb',
  originalPriceText: (_waiMode: boolean) => 'ml-[6px] line-through f12 font-br text-primary-black',

  // 包含所有二级内容的盒子
  brandBox: 'w-full px-[7px] xl:px-0 flex flex-col items-center justify-center',
  // 二级选项盒子（二级选项最外层）
  brandItem:
    'w-full bg-[#f5f7fa] xl:bg-white xl:border xl:border-t-[#d4d4d4] max-xl:rounded-[5px] border xl:border-l-0 xl:border-r-0 box-border max-xl:mb-2.5 py-[14px] px-[10px] xl:px-[17px] cursor-pointer max-xl:hover:border-[#d4d4d4] max-xl:hover:border-[1px] xl:border-[1px] xl:border-[transparent] xl:flex xl:items-center',
  selectedBrandOptionBox:
    'max-xl:border-[1.5px] max-xl:!border-primary-purple max-xl:hover:border-primary-purple xl:!bg-[rgba(70,58,232,0.05)]',
  // 二级选项内容容器（仅次于二级选项盒子，当前包含内容跟二级选项盒子一样）
  brandItemInner: 'w-full text-primary-black text-sm relative',

  // 折扣标签
  discountBadge: (applyMembershipLevel?: ApplyMembershipLevel | number | null) => {
    const common =
      'absolute top-0 right-0 h-[15px] px-1.5 text-primary-black rounded-[0px_4px_0px_6px] f11 font-br';
    const bgMap: Record<ApplyMembershipLevel, string> = {
      '-1': 'bg-[#fca6ff]',
      0: 'bg-[#f0dbd8]',
      1: 'bg-[#d8e3f0]',
      2: 'bg-[#fcddb6]',
      3: 'bg-gradient-to-r from-[#e4dfff] to-[#bcbcff]',
    };
    const bg = bgMap[applyMembershipLevel as ApplyMembershipLevel] ?? bgMap[-1];

    return `${common} ${bg}`;
  },

  // 颜色选择器
  colorSelector: 'flex flex-wrap items-center justify-start self-start pt-[12px]',
  colorItem:
    'flex items-center justify-center mr-[12px] mb-[5px] h-[22px] w-[22px] box-border border rounded-full cursor-pointer',
  selectedColorItem: 'border-primary-purple hover:border-primary-purple',
  unselectedColorItem: 'border-transparent',
  colorCategoryBox: 'w-full px-[7px] xl:px-0 flex flex-col items-center justify-center',
  colorCategoryItem:
    'w-full bg-[#f5f7fa] xl:bg-white xl:border xl:border-t-[#d4d4d4] max-xl:rounded-[5px] border xl:border-l-0 xl:border-r-0 box-border max-xl:mb-2.5 py-[14px] px-[10px] xl:px-[17px] cursor-pointer max-xl:hover:border-[#d4d4d4] max-xl:hover:border-[1px] xl:border-[1px] xl:border-[transparent]',
  selectedColorCategoryItem:
    'max-xl:border-[1.5px] max-xl:!border-primary-purple max-xl:hover:border-primary-purple xl:!bg-[rgba(70,58,232,0.05)]',
  colorCategoryTitle: 'f14 font-bs text-primary-black',
  lensTypeSecondaryArrow:
    'absolute right-0 top-1/2 -translate-y-1/2 transform transition-transform duration-200 text-gray f16 max-xl:hidden',
  lensTypeSecondaryArrowMobile:
    'absolute right-0 transform transition-transform duration-200 text-gray f16 xl:hidden',

  // 空状态
  emptyState: 'flex flex-col items-center justify-center pt-[53px] pb-[100px]',
  emptyStateText: 'pt-[8px] xl:pt-[12px] f12 text-primary-black text-center',
  emptyStateButton: '!mt-[20px] text-white f12 !font-bs cursor-pointer',

  // 处方类型组件特有样式(处方类型样式跟其他步骤选项样式有点不一样，暂时不统一)
  prescriptionTypeOption:
    'mb-[10px] rounded-[6px] overflow-hidden border border-[#d4d4d4] bg-white cursor-pointer hover:border-[#d4d4d4] hover:border-[1px] xl:border-[1px] xl:border-[transparent] xl:hover:border-[#d4d4d4]',
  prescriptionTypeOptionSelected:
    'border-[1.5px] !border-primary-purple hover:border-primary-purple',
  prescriptionTypeOptionHover: 'border-[#d4d4d4] hover:border-primary-purple',
  prescriptionTypeContent: 'relative flex items-start p-[21px_12px] xl:p-[14px_16px] rounded-[6px]',
  prescriptionTypeImage: 'mr-[12px] mr-[20px] w-[40px] h-[40px] xl:w-[48px] xl:h-[48px]',
  prescriptionTypeTextContainer: 'flex flex-col flex-1',
  prescriptionTypeTitle: 'flex items-center mb-[10px]',
  prescriptionTypeTitleText: 'f14 font-bs',
  prescriptionTypeDescription: (_waiMode: boolean) => 'f12 text-primary-black max-xl:max-w-[320px]',
  prescriptionTypeArrow: 'pt-[5px] pl-[15px] transition-all',
  prescriptionTypeArrowRotated: 'rotate-[-90deg]',

  // 子选项样式
  prescriptionSubOption:
    'relative p-[17px_15px] border-t border-[#d4d4d4] cursor-pointer xl:hover:bg-[rgba(70,58,232,0.05)] flex items-start',
  prescriptionSubOptionSelected: 'bg-[rgba(70,58,232,0.05)]',
  prescriptionSubOptionTitle: 'mb-[10px] f14 font-bs',
  prescriptionSubOptionDescription: 'f12 text-primary-black max-xl:max-w-[290px]',
};

export default stepCommonClassName;
